import { useEffect, useState } from "react";
import { MicOff, Pause, Users, PhoneForwarded, PhoneOff, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useCallTimer } from "@/hooks/useCallTimer";

// Live-call control bar (Mute / Hold / Conference / Transfer / Hangup+dispose).
// Purely event-driven — it dispatches the same window events the Softphone +
// CallTransferControls already listen to, so it works identically whether it's
// embedded in the omni ConversationView or the voice-only CRM panel.
export const LiveCallControlBar = ({ className = "", status = null, aiMonitoring = false, timer = undefined }) => {
  const [confActive, setConfActive] = useState(false);
  const [transferConfUsed, setTransferConfUsed] = useState(false);
  const internal = useCallTimer(status === "dead");
  // Prefer the timer driven by an always-mounted parent (voice-only mode passes
  // it from Workspace); fall back to the internal hook otherwise.
  const mmss = timer != null ? timer : internal.mmss;
  const timerActive = timer != null ? true : internal.active;

  useEffect(() => {
    const onConf = (e) => setConfActive(!!e?.detail?.active);
    const onTcUsed = () => setTransferConfUsed(true);
    const onTcReset = () => setTransferConfUsed(false);
    const onNewCall = () => setTransferConfUsed(false);
    window.addEventListener("c7:conference-active", onConf);
    window.addEventListener("c7:transfer-conf-used", onTcUsed);
    window.addEventListener("c7:transfer-conf-reset", onTcReset);
    window.addEventListener("dialer:bridged", onNewCall);
    window.addEventListener("dialer:ended", onNewCall);
    return () => {
      window.removeEventListener("c7:conference-active", onConf);
      window.removeEventListener("c7:transfer-conf-used", onTcUsed);
      window.removeEventListener("c7:transfer-conf-reset", onTcReset);
      window.removeEventListener("dialer:bridged", onNewCall);
      window.removeEventListener("dialer:ended", onNewCall);
    };
  }, []);

  const fireHangupLeg = (scope) => {
    try { window.dispatchEvent(new CustomEvent("c7:hangup-leg", { detail: { scope } })); } catch {}
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} data-testid="live-call-control-bar">
      {[
        { I: MicOff, k: "mute", label: "Mute", evt: "c7:call-control", detail: { action: "mute" } },
        { I: Pause, k: "hold", label: "Hold", evt: "c7:call-control", detail: { action: "hold" } },
        { I: Users, k: "conference", label: "Conference", evt: "c7:open-conference", once: true },
        { I: PhoneForwarded, k: "transfer", label: "Transfer", evt: "c7:open-transfer", once: true },
      ].map((b) => {
        const isDisabled = b.once && transferConfUsed;
        return (
          <Button
            key={b.k}
            data-testid={`call-${b.k}`}
            variant="outline"
            size="icon"
            disabled={isDisabled}
            className="rounded-md w-10 h-10 disabled:opacity-40"
            title={isDisabled ? "Transfer/Conference already used on this call" : b.label}
            onClick={() => { try { window.dispatchEvent(new CustomEvent(b.evt, b.detail ? { detail: b.detail } : undefined)); } catch {} }}
          >
            <b.I className="w-4 h-4" />
          </Button>
        );
      })}
      {confActive ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="call-hangup-split" size="icon" className="rounded-md w-10 h-10 bg-red-500 hover:bg-red-600 text-white" title="Hangup options">
              <PhoneOff className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem data-testid="conv-hangup-customer" onClick={() => fireHangupLeg("customer")}>Hang up customer</DropdownMenuItem>
            <DropdownMenuItem data-testid="conv-hangup-third" onClick={() => fireHangupLeg("third")}>Hang up 3rd party</DropdownMenuItem>
            <DropdownMenuItem data-testid="conv-hangup-leave" onClick={() => fireHangupLeg("leave")}>Leave conference &amp; dispose</DropdownMenuItem>
            <DropdownMenuItem data-testid="conv-hangup-all" onClick={() => fireHangupLeg("all")} className="text-red-600">Hang up all lines</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Button
          data-testid="call-hangup"
          size="icon"
          className="rounded-md w-10 h-10 bg-red-500 hover:bg-red-600 text-white"
          title="Hangup & disposition"
          onClick={() => {
            try {
              window.dispatchEvent(new CustomEvent("workspace:hangup", { detail: { source: "control-bar" } }));
            } catch {}
          }}
        >
          <PhoneOff className="w-4 h-4" />
        </Button>
      )}
      {(status || aiMonitoring) && (
        <div className="ml-auto flex items-center gap-2">
          {status && (
            <Badge
              data-testid="call-status-badge"
              variant="outline"
              className={`gap-1.5 ${status === "dead"
                ? "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300"
                : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"}`}
            >
              <span className={`w-2 h-2 rounded-full ${status === "dead" ? "bg-red-500" : "bg-emerald-500 animate-pulse"}`} />
              {status === "dead" ? "Call ended" : "Live call"}
              {timerActive && (
                <span data-testid="call-timer" className="ml-1 font-mono tabular-nums font-semibold">{mmss}</span>
              )}
            </Badge>
          )}
          {aiMonitoring && (
            <Badge variant="outline" className="gap-1.5 border-secondary/30 bg-secondary/10 text-secondary dark:text-accent">
              <Bot className="w-3 h-3" /> AI monitoring
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveCallControlBar;
